import * as FileSaver from 'file-saver'

export const EXPORT_THEMES = [
  {
    key: 'ocean',
    label: '海洋蓝',
    headerFill: '#DCEBFF',
    subHeaderFill: '#EEF5FF',
    bodyFill: '#FFFFFF',
    accentFill: '#EAF7F2',
    headerFontColor: '#1F3A5F'
  },
  {
    key: 'forest',
    label: '森林绿',
    headerFill: '#DDF5E8',
    subHeaderFill: '#EEF9F3',
    bodyFill: '#FFFFFF',
    accentFill: '#F3FBF6',
    headerFontColor: '#174B35'
  },
  {
    key: 'sunset',
    label: '落日橙',
    headerFill: '#FFE6D6',
    subHeaderFill: '#FFF3EB',
    bodyFill: '#FFFFFF',
    accentFill: '#FFF8F3',
    headerFontColor: '#7A3E16'
  }
]

export function getThemeConfig(themeKey) {
  return EXPORT_THEMES.find((item) => item.key === themeKey) || EXPORT_THEMES[0]
}

export function buildExportFileName({ projectName, projectCode, dataLabel, startDate, endDate }) {
  const safeProject = (projectName || '项目').replace(/[\\/:*?"<>|]/g, '-')
  const safeLabel = (dataLabel || '数据').replace(/[\\/:*?"<>|]/g, '-')
  return `${safeProject}_${projectCode || 'unknown'}_${safeLabel}_${startDate}_${endDate}.xls`
}

export function exportWorkbook({ sheets, fileName }) {
  const workbookXml = buildWorkbookXml(sheets)
  const blob = new Blob([`\uFEFF${workbookXml}`], { type: 'application/vnd.ms-excel;charset=utf-8;' })
  FileSaver.saveAs(blob, fileName)
}

export function buildPivotSheet(options) {
  const {
    sheetName,
    orientation,
    dateList,
    metrics,
    items,
    valueMap,
    themeKey,
    includeMetricHeader = true
  } = options

  const theme = getThemeConfig(themeKey)
  return orientation === 'date-column'
    ? buildDateColumnSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName)
    : buildDateRowSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName)
}

export function buildTableSheetFromRows(options) {
  const {
    sheetName,
    rows = [],
    themeKey
  } = options
  const theme = getThemeConfig(themeKey)
  const columns = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => set.add(key))
      return set
    }, new Set())
  )
  const header = columns.map((key) => makeCell(key, 'header-main', 'String'))
  const bodyRows = rows.map((row) => columns.map((key, index) => {
    const value = row?.[key]
    const type = typeof value === 'number' ? 'Number' : 'String'
    return makeCell(value ?? '', index === 0 ? 'label' : 'body', type)
  }))

  const sheetRows = [header, ...bodyRows]
  return {
    name: sanitizeSheetName(sheetName),
    rows: sheetRows,
    merges: [],
    colWidths: computeColWidths(sheetRows),
    rowHeights: sheetRows.map((_, index) => (index === 0 ? 24 : 22)),
    theme
  }
}

export function buildGroupedDateReportSheet(options) {
  const {
    sheetName,
    dateList = [],
    groups = [],
    includeDailySumRow = false,
    includeDedupRow = false,
    themeKey
  } = options

  const theme = getThemeConfig(themeKey)
  const rows = []
  const merges = []

  const head1 = [makeCell('日期', 'header-main', 'String')]
  const head2 = [makeCell('日期', 'header-sub', 'String')]
  let col = 1

  groups.forEach((group) => {
    const metrics = group.metrics || []
    if (!metrics.length) return
    head1.push(makeCell(group.label, 'header-main', 'String'))
    for (let i = 1; i < metrics.length; i += 1) {
      head1.push(makePlaceholderCell())
    }
    metrics.forEach((metric) => {
      head2.push(makeCell(metric.label, 'header-sub', 'String'))
    })
    merges.push({ row: 0, col, across: metrics.length - 1, down: 0 })
    col += metrics.length
  })

  rows.push(head1, head2)

  dateList.forEach((date) => {
    const row = [makeCell(date, 'label', 'String')]
    groups.forEach((group) => {
      group.metrics.forEach((metric) => {
        row.push(makeCell(group.valuesByDate?.[date]?.[metric.key] ?? 0, 'body', 'Number'))
      })
    })
    rows.push(row)
  })

  if (includeDailySumRow) {
    const row = [makeCell('按天累加数据', 'label', 'String')]
    groups.forEach((group) => {
      group.metrics.forEach((metric) => {
        row.push(makeCell(group.summary?.dailySum?.[metric.key] ?? 0, 'body', 'Number'))
      })
    })
    rows.push(row)
  }

  if (includeDedupRow) {
    const row = [makeCell('去重数据', 'label', 'String')]
    groups.forEach((group) => {
      group.metrics.forEach((metric) => {
        row.push(makeCell(group.summary?.dedup?.[metric.key] ?? 0, 'body', 'Number'))
      })
    })
    rows.push(row)
  }

  return {
    name: sanitizeSheetName(sheetName),
    rows,
    merges,
    colWidths: computeColWidths(rows),
    rowHeights: rows.map((_, index) => (index < 2 ? 24 : 22)),
    theme
  }
}

function buildDateRowSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName) {
  const rows = []
  const merges = []

  if (metrics.length > 1 && includeMetricHeader) {
    const head1 = [makeCell('对象', 'header-main', 'String')]
    const head2 = [makeCell('对象', 'header-sub', 'String')]
    let col = 1
    dateList.forEach((date) => {
      head1.push(makeCell(date, 'header-main', 'String'))
      for (let i = 1; i < metrics.length; i += 1) {
        head1.push(makePlaceholderCell())
      }
      metrics.forEach((metric) => {
        head2.push(makeCell(metric.toUpperCase(), 'header-sub', 'String'))
      })
      merges.push({ row: 0, col, across: metrics.length - 1, down: 0 })
      col += metrics.length
    })
    rows.push(head1, head2)
  } else {
    rows.push([makeCell('对象', 'header-main', 'String'), ...dateList.map((date) => makeCell(date, 'header-main', 'String'))])
  }

  items.forEach((item) => {
    const row = [makeCell(item, 'label', 'String')]
    dateList.forEach((date) => {
      if (metrics.length > 1 && includeMetricHeader) {
        metrics.forEach((metric) => {
          row.push(makeCell(readMetricValue(valueMap, item, date, metric), 'body', 'Number'))
        })
      } else {
        row.push(makeCell(readMetricValue(valueMap, item, date, metrics[0]), 'body', 'Number'))
      }
    })
    rows.push(row)
  })

  return {
    name: sanitizeSheetName(sheetName),
    rows,
    merges,
    colWidths: computeColWidths(rows),
    rowHeights: rows.map((_, index) => (index < (metrics.length > 1 && includeMetricHeader ? 2 : 1) ? 24 : 22)),
    theme
  }
}

function buildDateColumnSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName) {
  const rows = []
  const merges = []

  if (metrics.length > 1 && includeMetricHeader) {
    const head1 = [makeCell('日期', 'header-main', 'String')]
    const head2 = [makeCell('日期', 'header-sub', 'String')]
    let col = 1
    items.forEach((item) => {
      head1.push(makeCell(item, 'header-main', 'String'))
      for (let i = 1; i < metrics.length; i += 1) {
        head1.push(makePlaceholderCell())
      }
      metrics.forEach((metric) => {
        head2.push(makeCell(metric.toUpperCase(), 'header-sub', 'String'))
      })
      merges.push({ row: 0, col, across: metrics.length - 1, down: 0 })
      col += metrics.length
    })
    rows.push(head1, head2)
  } else {
    rows.push([makeCell('日期', 'header-main', 'String'), ...items.map((item) => makeCell(item, 'header-main', 'String'))])
  }

  dateList.forEach((date) => {
    const row = [makeCell(date, 'label', 'String')]
    items.forEach((item) => {
      if (metrics.length > 1 && includeMetricHeader) {
        metrics.forEach((metric) => {
          row.push(makeCell(readMetricValue(valueMap, item, date, metric), 'body', 'Number'))
        })
      } else {
        row.push(makeCell(readMetricValue(valueMap, item, date, metrics[0]), 'body', 'Number'))
      }
    })
    rows.push(row)
  })

  return {
    name: sanitizeSheetName(sheetName),
    rows,
    merges,
    colWidths: computeColWidths(rows),
    rowHeights: rows.map((_, index) => (index < (metrics.length > 1 && includeMetricHeader ? 2 : 1) ? 24 : 22)),
    theme
  }
}

function readMetricValue(valueMap, item, date, metric) {
  return valueMap?.[item]?.[date]?.[metric] ?? 0
}

function makeCell(value, role, type) {
  return { value, role, type }
}

function makePlaceholderCell() {
  return { placeholder: true }
}

function computeColWidths(rows) {
  const maxCols = Math.max(...rows.map((row) => row.length), 0)
  return Array.from({ length: maxCols }, (_, colIndex) => {
    let maxLength = 10
    rows.forEach((row) => {
      if (row[colIndex]?.placeholder) return
      const value = row[colIndex]?.value == null ? '' : String(row[colIndex].value)
      maxLength = Math.max(maxLength, value.length + 2)
    })
    return Math.min(maxLength * 7, 220)
  })
}

function buildWorkbookXml(sheets) {
  const styles = buildStyleXml()
  const worksheets = sheets.map((sheet) => buildWorksheetXml(sheet)).join('')

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
  <Author>Codex</Author>
  <Created>${escapeXml(new Date().toISOString())}</Created>
 </DocumentProperties>
 <ExcelWorkbook xmlns="urn:schemas-microsoft-com:office:excel">
  <ProtectStructure>False</ProtectStructure>
  <ProtectWindows>False</ProtectWindows>
 </ExcelWorkbook>
 ${styles}
 ${worksheets}
</Workbook>`
}

function buildStyleXml() {
  const themeStyles = EXPORT_THEMES.map((theme) => {
    const suffix = sanitizeStyleKey(theme.key)
    return `
  <Style ss:ID="header-main-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="13" ss:Color="${theme.headerFontColor}"/>
   <Interior ss:Color="${theme.headerFill}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="header-sub-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="11" ss:Color="${theme.headerFontColor}"/>
   <Interior ss:Color="${theme.subHeaderFill}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="label-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Left"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="10" ss:Color="#334155"/>
   <Interior ss:Color="${theme.accentFill}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="body-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Size="10" ss:Color="#334155"/>
   <Interior ss:Color="${theme.bodyFill}" ss:Pattern="Solid"/>
  </Style>`
  }).join('')

  return `<Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Size="10" ss:Color="#334155"/>
   <Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="header-main">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="13" ss:Color="#1F2937"/>
  </Style>
  <Style ss:ID="header-sub">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="11" ss:Color="#334155"/>
  </Style>
  <Style ss:ID="label">
   <Alignment ss:Vertical="Center" ss:Horizontal="Left"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="10" ss:Color="#334155"/>
  </Style>
  <Style ss:ID="body">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Size="10" ss:Color="#334155"/>
  </Style>
  ${themeStyles}
 </Styles>`
}

function buildWorksheetXml(sheet) {
  const rowsXml = sheet.rows
    .map((row, rowIndex) => {
      const height = sheet.rowHeights?.[rowIndex]
      let visualColIndex = 0
      const cellsXml = row
        .map((cell, colIndex) => {
          visualColIndex += 1
          if (cell?.placeholder) {
            return ''
          }
          const merge = sheet.merges.find((item) => item.row === rowIndex && item.col === colIndex)
          const styleId = resolveStyleId(cell, rowIndex, colIndex, sheet.theme)
          const mergeAcross = merge ? ` ss:MergeAcross="${merge.across}"` : ''
          const mergeDown = merge ? ` ss:MergeDown="${merge.down}"` : ''
          const dataType = cell.type || inferType(cell.value)
          const indexAttr = visualColIndex === colIndex + 1 ? '' : ` ss:Index="${visualColIndex}"`
          return `<Cell${indexAttr} ss:StyleID="${styleId}"${mergeAcross}${mergeDown}><Data ss:Type="${dataType}">${escapeXml(formatCellValue(cell.value, dataType))}</Data></Cell>`
        })
        .join('')
      const heightAttr = height ? ` ss:AutoFitHeight="0" ss:Height="${height}"` : ''
      return `<Row${heightAttr}>${cellsXml}</Row>`
    })
    .join('')

  const columnsXml = (sheet.colWidths || [])
    .map((width) => `<Column ss:AutoFitWidth="0" ss:Width="${width}"/>`)
    .join('')

  return `<Worksheet ss:Name="${escapeXml(sheet.name)}">
  <Table>${columnsXml}${rowsXml}</Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <Selected/>
   <ProtectObjects>False</ProtectObjects>
   <ProtectScenarios>False</ProtectScenarios>
  </WorksheetOptions>
 </Worksheet>`
}

function resolveStyleId(cell, rowIndex, colIndex, theme) {
  const baseRole = cell.role || 'body'
  if (!theme) return baseRole
  const suffix = sanitizeStyleKey(theme.key || 'theme')
  return `${baseRole}-${suffix}`
}

function sanitizeStyleKey(value) {
  return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '')
}

function inferType(value) {
  return typeof value === 'number' ? 'Number' : 'String'
}

function formatCellValue(value, type) {
  if (value == null) return ''
  if (type === 'Number') return Number(value)
  return String(value)
}

function escapeXml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function sanitizeSheetName(name) {
  return String(name || 'sheet').replace(/[\\/?*[\]:]/g, '_').slice(0, 28)
}
